<script setup>
import { useAlert } from 'dashboard/composables';
import AddAutomationRule from './AddAutomationRule.vue';
import EditAutomationRule from './EditAutomationRule.vue';
import AutomationPresetsModal from './AutomationPresetsModal.vue';
import { AUTOMATION_PRESETS } from './automationPresets';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import AutomationRuleRow from './AutomationRuleRow.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();
const confirmDialog = ref(null);

const loading = ref({});
const showAddPopup = ref(false);
const showEditPopup = ref(false);
const showPresetsModal = ref(false);
const previewPreset = ref(null);

const openPresetsModal = (preset = null) => {
  previewPreset.value = preset;
  showPresetsModal.value = true;
};
const closePresetsModal = () => {
  showPresetsModal.value = false;
  previewPreset.value = null;
};
const showDeleteConfirmationPopup = ref(false);
const selectedAutomation = ref({});
const toggleModalTitle = ref(t('AUTOMATION.TOGGLE.ACTIVATION_TITLE'));
const toggleModalDescription = ref(
  t('AUTOMATION.TOGGLE.ACTIVATION_DESCRIPTION')
);

const records = computed(() => getters['automations/getAutomations'].value);
const uiFlags = computed(() => getters['automations/getUIFlags'].value);
const accountId = computed(() => getters.getCurrentAccountId.value);

const activeCount = computed(() => records.value.filter(a => a.active).length);
const inactiveCount = computed(
  () => records.value.filter(a => !a.active).length
);

const deleteConfirmText = computed(
  () => `${t('AUTOMATION.DELETE.CONFIRM.YES')} ${selectedAutomation.value.name}`
);

const deleteRejectText = computed(
  () => `${t('AUTOMATION.DELETE.CONFIRM.NO')} ${selectedAutomation.value.name}`
);

const deleteMessage = computed(() => ` ${selectedAutomation.value.name}?`);

const isSLAEnabled = computed(() =>
  getters['accounts/isFeatureEnabledonAccount'].value(accountId.value, 'sla')
);

onMounted(() => {
  store.dispatch('inboxes/get');
  store.dispatch('agents/get');
  store.dispatch('contacts/get');
  store.dispatch('teams/get');
  store.dispatch('labels/get');
  store.dispatch('campaigns/get');
  store.dispatch('automations/get');
  if (isSLAEnabled.value) {
    store.dispatch('sla/get');
  }
});

const openAddPopup = () => {
  showAddPopup.value = true;
};

const applyPreset = async rule => {
  try {
    await store.dispatch('automations/create', rule);
    useAlert(t('AUTOMATION.PRESET.SUCCESS'));
    showPresetsModal.value = false;
  } catch {
    useAlert(t('AUTOMATION.PRESET.ERROR'));
  }
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = response => {
  selectedAutomation.value = response;
  showEditPopup.value = true;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  selectedAutomation.value = response;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteAutomation = async id => {
  try {
    await store.dispatch('automations/delete', id);
    useAlert(t('AUTOMATION.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AUTOMATION.DELETE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[selectedAutomation.value.id] = false;
  }
};
const confirmDeletion = () => {
  loading.value[selectedAutomation.value.id] = true;
  closeDeletePopup();
  deleteAutomation(selectedAutomation.value.id);
};
const cloneAutomation = async ({ id }) => {
  try {
    await store.dispatch('automations/clone', id);
    useAlert(t('AUTOMATION.CLONE.API.SUCCESS_MESSAGE'));
    store.dispatch('automations/get');
  } catch (error) {
    useAlert(t('AUTOMATION.CLONE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[selectedAutomation.value.id] = false;
  }
};

const submitAutomation = async (payload, mode) => {
  try {
    const action =
      mode === 'edit' ? 'automations/update' : 'automations/create';
    const successMessage =
      mode === 'edit'
        ? t('AUTOMATION.EDIT.API.SUCCESS_MESSAGE')
        : t('AUTOMATION.ADD.API.SUCCESS_MESSAGE');
    await store.dispatch(action, payload);
    useAlert(successMessage);
    hideAddPopup();
    hideEditPopup();
  } catch (error) {
    const errorMessage =
      mode === 'edit'
        ? t('AUTOMATION.EDIT.API.ERROR_MESSAGE')
        : t('AUTOMATION.ADD.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};
const toggleAutomation = async ({ id, name, status }) => {
  try {
    if (status) {
      toggleModalTitle.value = t('AUTOMATION.TOGGLE.DEACTIVATION_TITLE');
      toggleModalDescription.value = t(
        'AUTOMATION.TOGGLE.DEACTIVATION_DESCRIPTION',
        {
          automationName: name,
        }
      );
    } else {
      toggleModalTitle.value = t('AUTOMATION.TOGGLE.ACTIVATION_TITLE');
      toggleModalDescription.value = t(
        'AUTOMATION.TOGGLE.ACTIVATION_DESCRIPTION',
        {
          automationName: name,
        }
      );
    }

    const ok = await confirmDialog.value.showConfirmation();
    if (ok) {
      await store.dispatch('automations/update', {
        id: id,
        active: !status,
      });
      const message = status
        ? t('AUTOMATION.TOGGLE.DEACTIVATION_SUCCESFUL')
        : t('AUTOMATION.TOGGLE.ACTIVATION_SUCCESFUL');
      useAlert(message);
    }
  } catch (error) {
    useAlert(t('AUTOMATION.EDIT.API.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('AUTOMATION.LOADING')"
    :no-records-found="false"
    :no-records-message="$t('AUTOMATION.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('AUTOMATION.HEADER')"
        :description="$t('AUTOMATION.DESCRIPTION')"
        :link-text="$t('AUTOMATION.LEARN_MORE')"
        feature-name="automation"
      >
        <template #actions>
          <Button
            icon="i-lucide-layout-template"
            variant="clear"
            :label="$t('AUTOMATION.PRESET.BUTTON')"
            @click="openPresetsModal()"
          />
          <Button
            icon="i-lucide-circle-plus"
            :label="$t('AUTOMATION.HEADER_BTN_TXT')"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <div class="flex flex-col gap-8">
        <!-- Rules section -->
        <section>
          <!-- Active/inactive counter -->
          <div
            v-if="records.length"
            class="flex items-center gap-4 mb-4 text-sm text-n-slate-9"
          >
            <span class="flex items-center gap-1.5">
              <span class="size-2 rounded-full bg-green-500" />
              <span class="font-semibold text-n-slate-12">{{
                activeCount
              }}</span>
              {{ $t('AUTOMATION.LIST.ACTIVE_LABEL') }}
            </span>
            <span class="flex items-center gap-1.5">
              <span class="size-2 rounded-full bg-n-slate-5" />
              <span class="font-semibold text-n-slate-12">{{
                inactiveCount
              }}</span>
              {{ $t('AUTOMATION.LIST.INACTIVE_LABEL') }}
            </span>
          </div>

          <!-- Empty CTA -->
          <button
            v-if="!records.length"
            type="button"
            class="flex items-center gap-3 p-4 rounded-xl border border-dashed border-n-weak bg-n-solid-1 hover:border-n-strong hover:bg-n-alpha-1 transition-all duration-150 w-full text-left"
            @click="openAddPopup"
          >
            <span class="i-lucide-zap size-5 text-n-brand shrink-0" />
            <div>
              <p class="text-sm font-semibold text-n-slate-12">
                {{ $t('AUTOMATION.PRESET.EMPTY_TITLE') }}
              </p>
              <p class="text-xs text-n-slate-11 mt-0.5">
                {{ $t('AUTOMATION.PRESET.EMPTY_DESC') }}
              </p>
            </div>
          </button>

          <!-- Rules grid -->
          <div
            v-else
            class="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3"
          >
            <AutomationRuleRow
              v-for="automation in records"
              :key="automation.id"
              :automation="automation"
              :loading="loading[automation.id]"
              @clone="cloneAutomation"
              @toggle="toggleAutomation"
              @edit="openEditPopup"
              @delete="openDeletePopup"
            />
          </div>
        </section>

        <!-- Templates section -->
        <section>
          <div class="flex items-center gap-2 mb-3">
            <h3
              class="text-xs font-semibold tracking-widest uppercase text-n-slate-11"
            >
              {{ $t('AUTOMATION.PRESET.SECTION_LABEL') }}
            </h3>
            <span class="text-xs font-semibold text-n-slate-11">
              {{ AUTOMATION_PRESETS.length }}
            </span>
          </div>
          <div class="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2.5">
            <button
              v-for="preset in AUTOMATION_PRESETS"
              :key="preset.id"
              type="button"
              class="relative flex flex-col items-center justify-center gap-1.5 aspect-square p-3 rounded-xl border border-dashed border-n-weak bg-n-solid-1 hover:border-n-strong hover:bg-n-alpha-1 transition-all duration-150 group"
              @click="openPresetsModal(preset)"
            >
              <span class="text-2xl leading-none">{{ preset.icon }}</span>
              <span
                class="text-xs font-semibold text-n-slate-12 truncate text-center w-full leading-snug"
              >
                {{ preset.name }}
              </span>
              <span
                class="text-[10px] text-n-slate-10 text-center truncate w-full"
              >
                {{ preset.eventLabel }}
              </span>
              <span
                class="absolute top-1.5 right-1.5 invisible group-hover:visible text-[10px] font-medium text-woot-600"
              >
                {{ $t('AUTOMATION.PRESET.PREVIEW') }}
              </span>
            </button>
          </div>
        </section>
      </div>
    </template>

    <woot-modal
      v-model:show="showAddPopup"
      size="medium"
      :on-close="hideAddPopup"
    >
      <AddAutomationRule
        v-if="showAddPopup"
        :on-close="hideAddPopup"
        @save-automation="submitAutomation"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('LABEL_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('AUTOMATION.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />

    <woot-modal
      v-model:show="showEditPopup"
      size="medium"
      :on-close="hideEditPopup"
    >
      <EditAutomationRule
        v-if="showEditPopup"
        :on-close="hideEditPopup"
        :selected-response="selectedAutomation"
        @save-automation="submitAutomation"
      />
    </woot-modal>
    <woot-confirm-modal
      ref="confirmDialog"
      :title="toggleModalTitle"
      :description="toggleModalDescription"
    />

    <woot-modal
      v-model:show="showPresetsModal"
      size="medium"
      :on-close="closePresetsModal"
    >
      <AutomationPresetsModal
        v-if="showPresetsModal"
        :initial-preset="previewPreset"
        @use-preset="applyPreset"
      />
    </woot-modal>
  </SettingsLayout>
</template>
